import { Injectable, computed, inject, signal } from '@angular/core';
import {
  Observable,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  of,
  tap,
  throwError,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { LoginResponse } from '@ls-commons/auth/model';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SessionExpirationDialogComponent } from './session-expiration-dialog/session-expiration-dialog.component';

export interface UserJWT {
  name: string;
  surname: string;
  role: number;
  id: number;
  usuarioId?: number;
  image: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _currentUser = signal<UserJWT | null>(null);
  public currentUser = computed(() => this._currentUser());

  public timeExpiration = signal<number>(0);
  public timeExpirationMessage = computed(
    () =>
      `La sesión expirará en menos de ${this.timeExpiration()}
        ${this.timeExpiration() <= 1 ? 'minuto' : 'minutos'}`
  );

  private checkInterval: any;
  private warnedAboutExpiry = false;
  ref: DynamicDialogRef | undefined;

  readonly http = inject(HttpClient);
  readonly router = inject(Router);
  socialAuthService = inject(SocialAuthService);
  dialogService = inject(DialogService);

  googleToken = '';

  private _forcedClientId: string | null = null;

  public get forcedClientId() {
    return this._forcedClientId;
  }
  public set forcedClientId(value: string | null) {
    this._forcedClientId = value;
  }

  socialAuthState$ = this.socialAuthService.authState.pipe(
    map((user: SocialUser) => user?.idToken),
    distinctUntilChanged(),
    filter(token => token !== this.googleToken),
    tap(token => (this.googleToken = token))
  );

  constructor() {
    const token = this.getToken();
    token && this.retriveUserFromJWT(token);
  }

  retriveUserFromJWT(token: string) {
    const helper = new JwtHelperService();
    const tokenData = helper.decodeToken(token);

    this._currentUser.set({
      name: tokenData.name,
      surname: tokenData.surname,
      role: tokenData.role,
      id: tokenData.id,
      image: tokenData.image,
    });
  }

  login(email: string, password: string): Observable<LoginResponse> {
    const url = 'usuario/login';
    const body = { email, contrasena: password };

    return this.http
      .post<LoginResponse>(url, body)
      .pipe(catchError(err => this.handleError(err, 'Login')));
  }

  refreshToken(): Observable<boolean> {
    const url = 'usuario/login/renew';

    return this.http.get<LoginResponse>(url).pipe(
      map((resp: LoginResponse) => {
        this.setAuthentication(null, resp.token);
        this.startTokenWatcher(resp.token);
        return true;
      }),
      catchError(() => of(false))
    );
  }

  loginGoogle(token: string): Observable<LoginResponse> {
    const url = 'usuario/login-google';
    const body = { token };

    return this.http
      .post<LoginResponse>(url, body)
      .pipe(catchError(err => this.handleError(err, 'Login Google')));
  }

  resetPassword(email: string): Observable<Response> {
    const url = 'usuario/restablecer-contrasena';
    const body = { email };

    return this.http
      .post<Response>(url, body)
      .pipe(catchError(err => this.handleError(err, 'Reset Password')));
  }

  checkTokenReset(token: string): Observable<Response> {
    const url = `usuario/restablecer-contrasena/${token}`;

    return this.http
      .get<Response>(url)
      .pipe(catchError(err => this.handleError(err, 'Check Token Reset')));
  }

  newPassword(token: string, password: string): Observable<Response> {
    const url = `usuario/restablecer-contrasena/${token}`;
    const body = { token, password };
    return this.http
      .post<Response>(url, body)
      .pipe(catchError(err => this.handleError(err, 'New Password')));
  }

  removeStorage() {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
  }

  logout() {
    this.forcedClientId = null;
    clearInterval(this.checkInterval);
    this.removeStorage();
    this.router.navigate(['/']);
    this.socialAuthService.signOut();
    this.warnedAboutExpiry = false;
  }

  setAuthentication(remember: boolean | null, token: string) {
    if (remember === null) {
      remember = localStorage.getItem('token') ? true : false;
    }
    this.removeStorage();
    remember
      ? localStorage.setItem('token', token)
      : sessionStorage.setItem('token', token);
    this.retriveUserFromJWT(token);
    this.startTokenWatcher(token);
  }

  getToken() {
    return (
      localStorage.getItem('token') || sessionStorage.getItem('token') || ''
    );
  }

  private startTokenWatcher(token: string) {
    clearInterval(this.checkInterval); // por si ya existía

    const helper = new JwtHelperService();
    const decoded = helper.decodeToken(token);

    const expiration = decoded.exp * 1000;

    this.checkInterval = setInterval(() => {
      const now = Date.now();
      const timeLeftMs = expiration - now;
      this.timeExpiration.set(Math.ceil(timeLeftMs / 1000 / 60));

      // Token caducado
      if (timeLeftMs <= 0) {
        this.logout();
      }

      // Si faltan menos de 5 minutos y no hemos avisado aún
      if (timeLeftMs <= 5 * 60 * 1000 && !this.warnedAboutExpiry) {
        this.warnedAboutExpiry = true;

        this.showSessionWarning().then((action: 'extend' | 'logout' | '') => {
          if (action === 'extend') {
            this.refreshToken().subscribe((refreshed: boolean) => {
              if (refreshed) {
                this.warnedAboutExpiry = false;
              }
              // Si no se ha podido refrescar la sesión, cerramos sesión
              // y redirigimos al login
              if (!refreshed) {
                this.logout();
              }
            });
          } else if (action === 'logout') {
            this.logout();
          }
        });
      }
    }, 60 * 1000); // Comprobar cada minuto
  }

  showSessionWarning(): Promise<'extend' | 'logout'> {
    this.ref = this.dialogService.open(SessionExpirationDialogComponent, {
      dismissableMask: false,
      width: '400px',
      closable: false,
      modal: true,
      styleClass: 'session-expiration-dialog',
    });

    return new Promise(resolve => {
      this.ref?.onClose.subscribe((result: 'extend' | 'logout') => {
        resolve(result);
      });
    });
  }

  isTokenExpired(): boolean {
    const helper = new JwtHelperService();
    const token = this.getToken();
    return token ? helper.isTokenExpired(token) : true;
  }

  private handleError(err: any, error: string) {
    return throwError(() =>
      err.status === 400 ? err.error.msg : `Se ha producido un error: ${error}`
    );
  }
}
